import React, { useState, useRef } from 'react';
import { 
  FaCalendarAlt, 
  FaList, 
  FaFileExcel, 
  FaFileImport, 
  FaSearch, 
  FaFilter, 
  FaTrash
} from 'react-icons/fa';
import * as XLSX from 'xlsx';

const UnifiedScheduler = ({ appointments, onStatusChange, onDelete }) => {
  const [viewMode, setViewMode] = useState('calendar'); // 'calendar' or 'list'
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const fileInputRef = useRef(null);

  // Status mapping colors helper
  const getStatusStyles = (status) => {
    switch (status) {
      case 'confirmed':
      case 'completed':
        return {
          bg: 'bg-green-50  text-green-700  border-green-200 ',
          dot: 'bg-green-500',
          hex: '#059669'
        };
      case 'cancelled':
      case 'no-show':
        return {
          bg: 'bg-red-50  text-red-700  border-red-200 ',
          dot: 'bg-red-500',
          hex: '#e11d48'
        };
      case 'pending':
      case 'rescheduled':
      case 'in-progress':
      default:
        return {
          bg: 'bg-amber-50  text-amber-700  border-amber-200 ',
          dot: 'bg-amber-500',
          hex: '#d97706'
        };
    }
  };

  // Export to Excel
  const handleExport = () => {
    const ws = XLSX.utils.json_to_sheet(appointments);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Appointments');
    XLSX.writeFile(wb, `appointments_export_${Date.now()}.xlsx`);
  };

  // Import from Excel
  const handleImport = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const data = evt.target?.result;
        const workbook = XLSX.read(data, { type: 'binary' });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const parsed = XLSX.utils.sheet_to_json(sheet);
        
        // Merge into appointments backup
        const current = JSON.parse(localStorage.getItem('appointments') || '[]');
        const updated = [...parsed, ...current];
        localStorage.setItem('appointments', JSON.stringify(updated));
        alert(`Successfully imported ${parsed.length} appointments! Refresh to populate.`);
      } catch (err) {
        alert('Failed parsing spreadsheet file. Ensure schema matches CRM export.');
      }
    };
    reader.readAsBinaryString(file);
  };

  // Filter list
  const filteredAppointments = appointments.filter(a => {
    const matchesSearch = 
      (a.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (a.email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (a.service || '').toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || a.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // Calendar calculations (Mock Current Month - May 2026)
  const currentMonthDays = Array.from({ length: 31 }, (_, i) => i + 1);
  const startDayOffset = 5; // Friday start for May 2026

  const getAppointmentsForDay = (day) => {
    const formattedDay = day < 10 ? `0${day}` : `${day}`;
    // Search dates like "2026-05-XX" or matching "May XX" or matching string contains "XX"
    return appointments.filter(a => {
      if (!a.date) return false;
      return a.date.includes(`-${formattedDay}`) || a.date.includes(`/${formattedDay}`) || a.date.endsWith(` ${day}`);
    });
  };

  return (
    <div className="glass-panel bg-white  rounded-xl border border-slate-200  p-5 flex flex-col">
      {/* Ribbon Toolbar */}
      <div className="flex flex-col md:flex-row justify-between items-stretch md:items-center gap-4 border-b border-slate-300  pb-4 mb-5">
        <div className="flex items-center gap-3">
          <div className="bg-purple-50  p-2.5 rounded-lg text-purple-600 ">
            <FaCalendarAlt size={18} />
          </div>
          <div>
            <h3 className="font-extrabold text-slate-800  text-base">Scheduler Board</h3>
            <p className="text-slate-400  text-xxs">Manage scheduling operations and logs</p>
          </div>
        </div>

        {/* Action controllers */}
        <div className="flex flex-wrap items-center gap-2">
          {/* File Excel actions */}
          <button
            onClick={handleExport}
            className="flex items-center gap-1.5 border border-slate-200  bg-white  hover:bg-slate-50 :bg-slate-900 text-slate-700  hover:text-green-600 hover:border-green-200 px-3 py-1.5 rounded-lg text-xs font-bold transition-all"
            title="Export spreadsheet logs"
          >
            <FaFileExcel className="text-slate-400 " /> Export
          </button>
          <button
            onClick={() => fileInputRef.current?.click()}
            className="flex items-center gap-1.5 border border-slate-200  bg-white  hover:bg-slate-50 :bg-slate-900 text-slate-700  hover:text-purple-600 hover:border-purple-200 px-3 py-1.5 rounded-lg text-xs font-bold transition-all"
            title="Import Excel records"
          >
            <FaFileImport className="text-slate-400 " /> Import
          </button>
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: 'none' }}
            accept=".xlsx,.xls"
            onChange={handleImport}
          />

          <span className="h-6 w-px bg-slate-200  mx-1"></span>

          {/* View Toggler */}
          <div className="border border-slate-200  rounded-lg p-0.5 flex bg-slate-50 ">
            <button
              onClick={() => setViewMode('calendar')}
              className={`p-1.5 rounded-md text-xs transition-all ${
                viewMode === 'calendar' ? 'bg-white  text-purple-600  shadow-md font-bold' : 'text-slate-500  hover:text-purple-600 :text-purple-400'
              }`}
            >
              <FaCalendarAlt size={13} />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-1.5 rounded-md text-xs transition-all ${
                viewMode === 'list' ? 'bg-white  text-purple-600  shadow-md font-bold' : 'text-slate-500  hover:text-purple-600 :text-purple-400'
              }`}
            >
              <FaList size={13} />
            </button>
          </div>
        </div>
      </div>

      {/* Interactive Controls (Search & Filter) */}
      <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-3 mb-5">
        <div className="relative flex-grow max-w-md">
          <input
            type="text"
            placeholder="Search patient, service, time..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="w-full bg-slate-50  border border-slate-200  rounded-lg py-2 pl-9 pr-4 text-xs focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-slate-800 "
          />
          <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400  text-xs" />
        </div>

        <div className="flex items-center gap-2">
          <button 
            onClick={() => setIsFilterOpen(!isFilterOpen)}
            className="flex items-center gap-1.5 border border-slate-200  bg-white  hover:bg-slate-50 :bg-slate-900 text-slate-700  px-3 py-2 rounded-lg text-xs font-bold"
          >
            <FaFilter size={11} className="text-slate-400 " /> Filters
          </button>
          
          {isFilterOpen && (
            <select
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
              className="bg-white  border border-slate-200  rounded-lg p-2 text-xs focus:ring focus:ring-purple-200 text-slate-800  focus:outline-none"
            >
              <option value="all">All Outcomes</option>
              <option value="confirmed">Confirmed</option>
              <option value="completed">Completed</option>
              <option value="pending">Pending</option>
              <option value="cancelled">Cancelled</option>
              <option value="no-show">No-Show</option>
            </select>
          )}
        </div>
      </div>

      {/* Grid Calendar Layout */}
      {viewMode === 'calendar' ? (
        <div className="flex-1">
          {/* Day Label Ribbon */}
          <div className="grid grid-cols-7 gap-2 mb-2 text-center text-xs font-bold text-slate-400  uppercase tracking-wider">
            <span>Sun</span><span>Mon</span><span>Tue</span><span>Wed</span><span>Thu</span><span>Fri</span><span>Sat</span>
          </div>

          {/* Calendar cell matrix */}
          <div className="grid grid-cols-7 gap-2">
            {/* Blank offsets */}
            {Array.from({ length: startDayOffset }).map((_, i) => (
              <div key={`offset-${i}`} className="bg-slate-50/30  border border-slate-300  rounded-lg min-h-[90px] opacity-40"></div>
            ))}

            {/* Days of month */}
            {currentMonthDays.map(day => {
              const dayAppts = getAppointmentsForDay(day);
              return (
                <div 
                  key={`day-${day}`} 
                  className="bg-white  border border-slate-300  hover:border-purple-200 :border-purple-800 rounded-lg p-2 min-h-[90px] flex flex-col justify-between transition-all hover:shadow-md"
                >
                  <span className="font-extrabold text-xs text-slate-400 ">{day}</span>
                  
                  {/* Slots scheduled */}
                  <div className="space-y-1 mt-2">
                    {dayAppts.slice(0, 2).map(appt => {
                      const style = getStatusStyles(appt.status);
                      return (
                        <div 
                          key={appt.id} 
                          className={`text-xxs px-1.5 py-0.5 rounded border ${style.bg} truncate font-medium flex items-center gap-1`}
                          title={`${appt.name} - ${appt.time} - ${appt.service}`}
                        >
                          <span className={`h-1.5 w-1.5 rounded-full ${style.dot}`}></span>
                          <span className="truncate">{appt.name.split(' ')[0]}</span>
                        </div>
                      );
                    })}
                    {dayAppts.length > 2 && (
                      <div className="text-xxs text-slate-400  text-center font-bold">
                        + {dayAppts.length - 2} more
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        /* Tabular List View */
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse text-xs">
            <thead>
              <tr className="border-b border-slate-300  text-slate-400  font-bold uppercase tracking-wider bg-slate-50/50 ">
                <th className="py-3 px-4">Patient</th>
                <th className="py-3 px-4">Service</th>
                <th className="py-3 px-4">Date</th>
                <th className="py-3 px-4">Time</th>
                <th className="py-3 px-4">Status Outcome</th>
                <th className="py-3 px-4 text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100  text-slate-700 ">
              {filteredAppointments.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-8 text-center text-slate-400 ">No appointments scheduled</td>
                </tr>
              ) : (
                filteredAppointments.map(appt => {
                  const style = getStatusStyles(appt.status);
                  return (
                    <tr key={appt.id} className="hover:bg-slate-50/30 :bg-slate-900/30 transition-colors">
                      <td className="py-3 px-4">
                        <div className="font-bold text-slate-800 ">{appt.name}</div>
                        <div className="text-slate-400  text-xxs">{appt.email}</div>
                      </td>
                      <td className="py-3 px-4 font-semibold text-slate-600 ">{appt.service}</td>
                      <td className="py-3 px-4 text-slate-500  font-medium">{appt.date}</td>
                      <td className="py-3 px-4 text-slate-500  font-medium">{appt.time}</td>
                      <td className="py-3 px-4">
                        <select
                          value={appt.status}
                          onChange={e => onStatusChange(appt.id, e.target.value)}
                          className={`px-3 py-1 rounded-full text-xxs font-bold border focus:outline-none focus:ring-2 focus:ring-purple-300 capitalize cursor-pointer ${style.bg}`}
                        >
                          <option value="pending">Pending</option>
                          <option value="confirmed">Confirmed</option>
                          <option value="completed">Completed</option>
                          <option value="cancelled">Cancelled</option>
                          <option value="no-show">No-Show</option>
                        </select>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <button
                          onClick={() => {
                            if (window.confirm(`Delete appointment for ${appt.name}?`)) {
                              onDelete(appt.id);
                            }
                          }}
                          className="p-1.5 text-slate-400  hover:text-rose-600 hover:bg-rose-50 :bg-rose-950/20 rounded-lg transition-colors"
                          title="Cancel/Remove slot"
                        >
                          <FaTrash size={12} />
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default UnifiedScheduler;
